/**
 * useWebSocket Hook
 * Manages WebSocket connection with auto-reconnect
 */
import { useEffect, useRef, useState, useCallback } from 'react';
export function useWebSocket({ enabled, onMessage }) {
    const [status, setStatus] = useState('disconnected');
    const wsRef = useRef(null);
    const reconnectAttemptRef = useRef(0);
    const reconnectTimerRef = useRef();
    const onMessageRef = useRef(onMessage);
    onMessageRef.current = onMessage;
    const connect = useCallback(() => {
        if (wsRef.current?.readyState === WebSocket.OPEN)
            return;
        const sessionToken = localStorage.getItem('sessionToken');
        if (!sessionToken)
            return;
        const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
        const host = import.meta.env.DEV ? 'localhost:8787' : 'api.ghnf.baskiers.dev';
        const url = `${protocol}//${host}/ws?session=${encodeURIComponent(sessionToken)}`;
        setStatus(reconnectAttemptRef.current > 0 ? 'reconnecting' : 'connecting');
        const ws = new WebSocket(url);
        wsRef.current = ws;
        ws.onopen = () => {
            setStatus('connected');
            reconnectAttemptRef.current = 0;
        };
        ws.onmessage = (event) => {
            try {
                const message = JSON.parse(event.data);
                onMessageRef.current?.(message);
            }
            catch {
                // Invalid message
            }
        };
        ws.onclose = () => {
            setStatus('disconnected');
            wsRef.current = null;
            if (enabled) {
                // Exponential backoff: 1s, 2s, 4s, 8s, max 30s
                const delay = Math.min(1000 * 2 ** reconnectAttemptRef.current, 30000);
                reconnectAttemptRef.current++;
                reconnectTimerRef.current = setTimeout(() => {
                    if (enabled)
                        connect();
                }, delay);
            }
        };
        ws.onerror = () => {
            ws.close();
        };
    }, [enabled]);
    const disconnect = useCallback(() => {
        if (reconnectTimerRef.current) {
            clearTimeout(reconnectTimerRef.current);
        }
        if (wsRef.current) {
            wsRef.current.close();
            wsRef.current = null;
        }
        reconnectAttemptRef.current = 0;
        setStatus('disconnected');
    }, []);
    useEffect(() => {
        if (enabled) {
            connect();
        }
        else {
            disconnect();
        }
        return () => {
            disconnect();
        };
    }, [enabled, connect, disconnect]);
    const sendMessage = useCallback((message) => {
        if (wsRef.current?.readyState === WebSocket.OPEN) {
            wsRef.current.send(JSON.stringify(message));
        }
    }, []);
    return { status, sendMessage, connect, disconnect };
}
